import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import chatAPI from '../api/chat';
import { API_BASE_URL } from '../api/tourism';

// Session inactivity timeout in milliseconds (5 minutes)
const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000;
// How often to check for inactivity (30 seconds)
const INACTIVITY_CHECK_INTERVAL_MS = 30 * 1000;

interface SessionContextType {
  sessionId: string | null;
  sessionToken: string | null;  // Reserved for future auth
  userId: string | null;
  metadata: Record<string, any>;
  setSessionId: (id: string, token?: string) => void;
  setUserCredentials: (userId: string, metadata?: Record<string, any>) => void;
  createNewSession: (userId?: string, metadata?: Record<string, any>) => Promise<void>;
  isCreatingSession: boolean;
  clearSession: () => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
};

interface SessionProviderProps {
  children: ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  const [sessionId, setSessionIdState] = useState<string | null>(null);
  const [sessionToken, setSessionTokenState] = useState<string | null>(null);
  const [userId, setUserIdState] = useState<string | null>(null);
  const [metadata, setMetadataState] = useState<Record<string, any>>({});
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  // Refs for stable access in event handlers (avoids stale closures)
  const sessionIdRef = useRef<string | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isCreatingRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { isCreatingRef.current = isCreatingSession; }, [isCreatingSession]);

  const setSessionId = useCallback((id: string, token?: string) => {
    console.log('🆔 Setting session ID:', id);
    setSessionIdState(id);
    sessionIdRef.current = id;
    lastActivityRef.current = Date.now();
    
    if (token !== undefined) {
      setSessionTokenState(token || null);
    }
  }, []);

  const setUserCredentials = useCallback((newUserId: string, newMetadata?: Record<string, any>) => {
    setUserIdState(newUserId);
    if (newMetadata) {
      setMetadataState(newMetadata);
    }
  }, []);

  const clearSession = useCallback(() => {
    console.log('🗑️  Clearing session data');
    // Notify backend (fire and forget)
    const currentSessionId = sessionIdRef.current;
    if (currentSessionId) {
      fetch(`${API_BASE_URL}/api/v1/chat/${encodeURIComponent(currentSessionId)}`, {
        method: 'DELETE',
      }).catch(() => {});
    }
    setSessionIdState(null);
    sessionIdRef.current = null;
    setSessionTokenState(null);
    setUserIdState(null);
    setMetadataState({});
  }, []);

  const createNewSession = useCallback(async (newUserId?: string, newMetadata?: Record<string, any>) => {
    if (isCreatingRef.current) return;
    
    isCreatingRef.current = true;
    setIsCreatingSession(true);
    try {
      console.log('🚀 Creating new session...');
      const sessionResponse = await chatAPI.createSession(newUserId, newMetadata);
      const newSessionId = sessionResponse.session_id;
      
      if (!newSessionId) {
        throw new Error('No session_id in response');
      }
      
      console.log('✅ New session created:', newSessionId);
      setSessionId(newSessionId, sessionResponse.session_token || '');
      lastActivityRef.current = Date.now();
      
      if (newUserId) {
        setUserCredentials(newUserId, newMetadata);
      }
    } catch (error) {
      console.error('❌ Error creating session:', error);
      // Fallback to client-generated ID
      const fallbackId = `session_${Math.random().toString(36).substring(2, 18).padEnd(16, '0')}_${Math.floor(Date.now() / 1000)}`;
      console.log('⚠️  Using fallback session ID:', fallbackId);
      setSessionId(fallbackId, '');
    } finally {
      isCreatingRef.current = false;
      setIsCreatingSession(false);
    }
  }, [setSessionId, setUserCredentials]);

  // Initialize session on mount + set up inactivity tracking + beforeunload
  useEffect(() => {
    // Create new session on every page load (memory-only per tab)
    createNewSession();

    // --- Inactivity tracking ---
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };

    // Listen for user activity events
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Periodically check for inactivity
    const inactivityInterval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastActivityRef.current;
      
      if (elapsed > INACTIVITY_TIMEOUT_MS && sessionIdRef.current && !isCreatingRef.current) {
        console.log('⏰ Session expired due to 5-minute inactivity. Creating new session...');
        // Clear old session and create new one
        const oldSessionId = sessionIdRef.current;
        if (oldSessionId) {
          fetch(`${API_BASE_URL}/api/v1/chat/${encodeURIComponent(oldSessionId)}`, {
            method: 'DELETE',
          }).catch(() => {});
        }
        setSessionIdState(null);
        sessionIdRef.current = null;
        setSessionTokenState(null);
        // Clear chat history from sessionStorage
        if (oldSessionId) {
          sessionStorage.removeItem(`chat_history_${oldSessionId}`);
        }
        lastActivityRef.current = Date.now();
        createNewSession();
      }
    }, INACTIVITY_CHECK_INTERVAL_MS);

    // --- Tab close cleanup ---
    const handleBeforeUnload = () => {
      const currentSessionId = sessionIdRef.current;
      if (currentSessionId) {
        // Use sendBeacon for reliable fire-and-forget on tab close
        const url = `${API_BASE_URL}/api/v1/chat/${encodeURIComponent(currentSessionId)}`;
        // sendBeacon only supports POST, so we use fetch with keepalive
        try {
          fetch(url, { method: 'DELETE', keepalive: true }).catch(() => {});
        } catch {
          // Ignore errors during unload
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Cleanup
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(inactivityInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [createNewSession]);

  const value: SessionContextType = {
    sessionId,
    sessionToken,
    userId,
    metadata,
    setSessionId,
    setUserCredentials,
    createNewSession,
    isCreatingSession,
    clearSession,
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};
